/**
 * 🔗 Admin Link Generation Panel
 * Generiert AWS/Search.app Links für normale Jobs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link2, RefreshCw, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AWS_REGION_OPTIONS, DEFAULT_AWS_REGIONS } from '../../constants/awsRegions';

const defaultLinkTypeConfig = (regions = DEFAULT_AWS_REGIONS) => ({
  provider: 'share.google',
  target: 'slot',
  slotIndex: '',
  customTargetsText: '',
  regions: [...regions],
  linkStyle: 'SHORT',
});

const LINK_TYPES = [
  { key: 'logo', label: 'Logo Links' },
  { key: 'cta', label: 'CTA Links' },
  { key: 'unsubscribe', label: 'Unsubscribe Links' }
];

const MIXED_STORY_PROVIDER = 'mixed-story';
const MIXED_STORY_RATIO = {
  'aws-hybrid': 37.5,
  'search-app-wrap': 62.5,
};

const AdminLinkPanel = ({
  jobId,
  onLinksGenerated,
  onLinksConfigured,
  isAdmin = false,
  excludedAwsRegions = [],
  enableQrCta = false,
  enableMixedStoryMode = false,
}) => {
  const excludedAwsRegionSet = new Set(excludedAwsRegions);
  const availableAwsRegionOptions = AWS_REGION_OPTIONS.filter(
    (option) => !excludedAwsRegionSet.has(option.value)
  );
  const availableDefaultAwsRegions = availableAwsRegionOptions.map(
    (option) => option.value
  );
  const normalizeAwsRegions = (regions) => {
    const selected = Array.isArray(regions)
      ? regions.filter((region) => availableDefaultAwsRegions.includes(region))
      : [];
    return selected.length > 0 ? selected : [...availableDefaultAwsRegions];
  };

  const [awsSlots, setAwsSlots] = useState([]);
  const [searchappSlots, setSearchappSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [linkConfig, setLinkConfig] = useState(() => ({
    logo: defaultLinkTypeConfig(availableDefaultAwsRegions),
    cta: defaultLinkTypeConfig(availableDefaultAwsRegions),
    unsubscribe: defaultLinkTypeConfig(availableDefaultAwsRegions),
  }));
  const [linkCounts, setLinkCounts] = useState({
    logo: 1,
    cta: 1,
    unsubscribe: 0
  });
  const [availableBrands, setAvailableBrands] = useState([]);

  useEffect(() => {
    axios.get('/api/searchapp-redirect/brands')
      .then(res => {
        if (res.data?.success && res.data.brands?.length > 0) {
          setAvailableBrands(res.data.brands);
        }
      })
      .catch(() => {});
  }, []);
  const [smtpEmail, setSmtpEmail] = useState('');
  const [configured, setConfigured] = useState(false);

  const normalizeTargetUrl = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const parseCustomTargets = (value) => {
    return String(value || '')
      .split(/[\n,]+/)
      .map(item => normalizeTargetUrl(item))
      .filter(Boolean);
  };

  const getSlotsForProvider = useCallback((provider) => {
    if (
      provider === 'aws'
      || provider === 'aws+searchapp'
      || provider === 'story-wrap'
      || provider === 'hybrid-aws-share.google'
      || provider === MIXED_STORY_PROVIDER
    ) {
      return awsSlots;
    }
    if (provider === 'search-app-wrap') {
      return searchappSlots;
    }
    return searchappSlots;
  }, [awsSlots, searchappSlots]);

  // Lade verfügbare Slots für AWS + Search.app
  const loadAllSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      const [awsRes, searchRes] = await Promise.all([
        axios.get('/api/aws-redirect/slots'),
        axios.get('/api/searchapp-redirect/slots')
      ]);
      const awsLoaded = awsRes.data.data?.slots || awsRes.data.slots || [];
      const searchLoaded = searchRes.data.data?.slots || searchRes.data.slots || [];
      setAwsSlots(awsLoaded);
      setSearchappSlots(searchLoaded);
    } catch (error) {
      console.error('Failed to load slots:', error);
      toast.error('Fehler beim Laden der Slots');
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    loadAllSlots();
  }, [loadAllSlots]);

  useEffect(() => {
    setLinkConfig(prev => {
      const next = { ...prev };
      LINK_TYPES.forEach(({ key }) => {
        const current = next[key];
        if (!current || current.target !== 'slot') {
          return;
        }
        let updated = current;
        const slots = getSlotsForProvider(current.provider);
        if (!Array.isArray(slots) || slots.length === 0) {
          return;
        }
        if (current.slotIndex === '' || !slots[parseInt(current.slotIndex, 10)]) {
          updated = { ...updated, slotIndex: '0' };
        }
        if (
          current.provider === MIXED_STORY_PROVIDER
          && searchappSlots.length > 0
          && (
            current.searchappWrapSlotIndex === ''
            || !searchappSlots[parseInt(current.searchappWrapSlotIndex, 10)]
          )
        ) {
          updated = { ...updated, searchappWrapSlotIndex: '0' };
        }
        if (updated !== current) {
          next[key] = updated;
        }
      });
      return next;
    });
  }, [awsSlots, searchappSlots, getSlotsForProvider]);

  useEffect(() => {
    setConfigured(false);
  }, [linkConfig, linkCounts, smtpEmail]);

  // placeholder - moved below getMaxForUser

  const isConfigReady = () => {
    for (const { key } of LINK_TYPES) {
      const count = Number(linkCounts[key]) || 0;
      if (count <= 0) {
        continue;
      }
      const current = linkConfig[key];
      if (!current) {
        return false;
      }
      if ((current.target || 'slot') === 'slot') {
        const slots = getSlotsForProvider(current.provider || 'aws');
        const slot = slots[parseInt(current.slotIndex, 10)];
        if (!slot) {
          return false;
        }
        if (current.provider === MIXED_STORY_PROVIDER) {
          const searchappWrapSlot = searchappSlots[
            parseInt(current.searchappWrapSlotIndex, 10)
          ];
          if (!searchappWrapSlot) {
            return false;
          }
        }
      } else {
        const targets = parseCustomTargets(current.customTargetsText);
        if (targets.length === 0) {
          return false;
        }
      }
    }
    return true;
  };

  const usesSearchApp = LINK_TYPES.some(({ key }) => {
    const provider = linkConfig[key]?.provider;
    return provider === 'searchapp'
      || provider === 'searchapp-s3'
      || provider === 'hybrid-aws-share.google'
      || provider === MIXED_STORY_PROVIDER;
  });


  useEffect(() => {
    setLinkConfig(prev => {
      let changed = false;
      const next = { ...prev };
      LINK_TYPES.forEach(({ key }) => {
        const current = next[key] || {};
        const isLogo = key === 'logo';
        const allowedProviders = isLogo
          ? [
              'aws',
              'searchapp',
              'searchapp-s3',
              'share.google',
              'hybrid-share.google',
              'hybrid-aws-share.google',
              'story-wrap',
              'search-app-wrap',
              ...(isAdmin && enableMixedStoryMode ? [MIXED_STORY_PROVIDER] : []),
              'brandlogo'
            ]
          : (isAdmin
            ? [
                'aws',
                'searchapp',
                'searchapp-s3',
                'share.google',
                'hybrid-share.google',
                'hybrid-aws-share.google',
                'story-wrap',
                'search-app-wrap',
                ...(enableMixedStoryMode && key === 'cta' ? [MIXED_STORY_PROVIDER] : []),
                ...(enableQrCta && key === 'cta' ? ['hybrid-share-qr'] : []),
                'aws+searchapp',
                'share.google+searchapp'
              ]
            : ['searchapp', 'searchapp-s3', 'share.google', 'hybrid-share.google', 'share.google+searchapp']);
        const provider = allowedProviders.includes(current.provider)
          ? current.provider
          : allowedProviders[0];
        const target = (!isAdmin && current.target === 'slot')
          ? 'custom'
          : (current.target || (isAdmin ? 'slot' : 'custom'));
        if (provider !== current.provider || target !== current.target) {
          changed = true;
        }
        next[key] = { ...current, provider, target };
      });
      return changed ? next : prev;
    });
  }, [isAdmin, enableQrCta, enableMixedStoryMode]);

  const handleConfigureLinks = async () => {
    const errors = [];
    const resolvedLinkConfig = {};

    LINK_TYPES.forEach(({ key, label }) => {
      const count = Number(linkCounts[key]) || 0;
      const current = linkConfig[key];
      if (!current || count <= 0) {
        return;
      }

      const provider = current.provider || 'aws';
      const target = current.target || 'slot';

      if (provider === 'brandlogo') {
        resolvedLinkConfig[key] = {
          provider,
          target: 'brandlogo',
          logo_brand: current.logo_brand || (availableBrands[0]?.slug || 'pobalogo'),
          brand_provider: current.brand_provider || 'share.google'
        };
      } else if (target === 'slot') {
        if (!isAdmin) {
          errors.push(`Slots sind nur für Admins verfügbar (${label})`);
          return;
        }
        const slots = getSlotsForProvider(provider);
        const slot = slots[parseInt(current.slotIndex, 10)];
        if (!slot || !Array.isArray(slot.domains) || slot.domains.length === 0) {
          errors.push(`Bitte wähle einen Slot für ${label}`);
          return;
        }
        const regions = normalizeAwsRegions(current.regions);
        const searchappWrapSlot = provider === MIXED_STORY_PROVIDER
          ? searchappSlots[parseInt(current.searchappWrapSlotIndex, 10)]
          : null;
        if (
          provider === MIXED_STORY_PROVIDER
          && (
            !searchappWrapSlot
            || !Array.isArray(searchappWrapSlot.domains)
            || searchappWrapSlot.domains.length === 0
          )
        ) {
          errors.push(`Bitte wähle einen Search-App-Wrap-Slot für ${label}`);
          return;
        }
        resolvedLinkConfig[key] = {
          provider,
          target,
          slot,
          ...(searchappWrapSlot ? { searchapp_wrap_slot: searchappWrapSlot } : {}),
          regions,
          region: regions[0],
          link_style: current.linkStyle || 'SHORT',
          ...(provider === MIXED_STORY_PROVIDER
            ? { mix_ratio: { ...MIXED_STORY_RATIO } }
            : {}),
        };
      } else {
        const targets = parseCustomTargets(current.customTargetsText);
        if (targets.length === 0) {
          errors.push(`Bitte gib Ziel‑URLs für ${label} ein`);
          return;
        }
        const regions = normalizeAwsRegions(current.regions);
        resolvedLinkConfig[key] = {
          provider,
          target,
          custom_targets: targets,
          regions,
          region: regions[0],
          link_style: current.linkStyle || 'SHORT',
          ...(provider === MIXED_STORY_PROVIDER
            ? { mix_ratio: { ...MIXED_STORY_RATIO } }
            : {}),
        };
      }
      
      // Dual-Provider: aws+searchapp → speichere auch die Search.app Config
      if (provider === 'aws+searchapp') {
        resolvedLinkConfig[key].dual_provider = true;
        resolvedLinkConfig[key].searchapp_link_style = current.linkStyle || 'SHORT';
        const regions = normalizeAwsRegions(current.regions);
        resolvedLinkConfig[key].regions = regions;
        resolvedLinkConfig[key].aws_region = regions[0];
      }
    });

    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    // Konfiguration speichern (wird beim Job-Submit verwendet)
    const config = {
      link_counts: linkCounts,
      link_config: resolvedLinkConfig,
      smtp_email: smtpEmail || null
    };
    
    setConfigured(true);
    
    if (onLinksConfigured) {
      onLinksConfigured(config);
    }
  };

  const getMaxForUser = useCallback((key, provider) => {
    if (isAdmin) return Infinity;
    if (
      provider === 'aws'
      || provider === 'hybrid-aws-share.google'
      || provider === 'story-wrap'
      || provider === 'search-app-wrap'
      || provider === MIXED_STORY_PROVIDER
    ) return 5;
    if (provider === 'searchapp-s3' && key === 'cta') return 30;
    return Infinity;
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    setLinkCounts(prev => {
      let changed = false;
      const next = { ...prev };
      LINK_TYPES.forEach(({ key }) => {
        const provider = linkConfig[key]?.provider || 'aws';
        const maxAllowed = getMaxForUser(key, provider);
        if (Number.isFinite(maxAllowed) && (Number(next[key]) || 0) > maxAllowed) {
          next[key] = maxAllowed;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [isAdmin, linkConfig, getMaxForUser]);

  const normalizeCount = (value) => {
    const parsed = parseInt(value, 10);
    const safe = Number.isFinite(parsed) ? parsed : 0;
    return Math.max(0, safe);
  };

  const updateLinkCount = (key, value) => {
    const nextValue = normalizeCount(value);
    const provider = linkConfig[key]?.provider || 'aws';
    const maxAllowed = getMaxForUser(key, provider);
    if (Number.isFinite(maxAllowed)) {
      const clamped = Math.min(nextValue, maxAllowed);
      if (nextValue > maxAllowed) {
        if (
          provider === 'aws'
          || provider === 'hybrid-aws-share.google'
          || provider === 'story-wrap'
          || provider === 'search-app-wrap'
          || provider === MIXED_STORY_PROVIDER
        ) {
          toast.error('Maximal 5 AWS‑Links pro Job für User');
        } else if (provider === 'searchapp-s3' && key === 'cta') {
          toast.error('Maximal 30 CTA‑Links (Search.app‑S3) pro Job für User');
        }
      }
      setLinkCounts(prev => ({ ...prev, [key]: clamped }));
      return;
    }
    setLinkCounts(prev => ({ ...prev, [key]: nextValue }));
  };

  useEffect(() => {
    if (isAdmin) return;
    setLinkCounts(prev => {
      let changed = false;
      const next = { ...prev };
      LINK_TYPES.forEach(({ key }) => {
        const provider = linkConfig[key]?.provider || 'aws';
        const maxAllowed = getMaxForUser(key, provider);
        if (Number.isFinite(maxAllowed) && (Number(next[key]) || 0) > maxAllowed) {
          next[key] = maxAllowed;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [isAdmin, linkConfig, getMaxForUser]);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border-2 border-purple-200 dark:border-purple-700 p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          🔗 Job Link Generation
        </h3>
      </div>

      {/* Link Routing */}
      <div className="space-y-4">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Link‑Routing pro Typ
        </div>
        {LINK_TYPES.map(({ key, label }) => {
          const current = linkConfig[key];
          const provider = current?.provider || 'aws';
          const target = current?.target || 'slot';
          const slots = getSlotsForProvider(provider);
          const customTargets = parseCustomTargets(current?.customTargetsText || '');
          const maxForUser = getMaxForUser(key, provider);
          const presetValues = (() => {
            if (!Number.isFinite(maxForUser)) {
              return [0, 1, 2, 3, 5, 10, 50, 100];
            }
            if (maxForUser === 5) {
              return [0, 1, 2, 3, 5];
            }
            if (maxForUser === 30) {
              return [0, 5, 10, 20, 30];
            }
            return [0, maxForUser];
          })();
          const providerOptions = key === 'logo'
            ? [
                { value: 'aws', label: 'AWS' },
                { value: 'searchapp', label: 'Search.app' },
                { value: 'searchapp-s3', label: 'Search.app‑S3' },
                { value: 'share.google', label: 'Share.Google' },
                { value: 'hybrid-share.google', label: 'Hybrid Share.Google' },
                { value: 'hybrid-aws-share.google', label: 'AWS Hybrid Search.app' },
                { value: 'story-wrap', label: 'Story-Wrap (stories.app.goo.gl)' },
                { value: 'search-app-wrap', label: 'Search-App-Wrap (search.app.goo.gl)' },
                ...(isAdmin && enableMixedStoryMode
                  ? [{ value: MIXED_STORY_PROVIDER, label: 'Mixxed 37,5/62,5 → Story-Makros' }]
                  : []),
                { value: 'brandlogo', label: 'Brand Logo' }
              ]
            : (isAdmin
              ? [
                  { value: 'aws', label: 'AWS' },
                  { value: 'searchapp', label: 'Search.app' },
                  { value: 'searchapp-s3', label: 'Search.app‑S3' },
                  { value: 'share.google', label: 'Share.Google' },
                  { value: 'hybrid-share.google', label: 'Hybrid Share.Google' },
                  { value: 'hybrid-aws-share.google', label: 'AWS Hybrid Search.app' },
                  { value: 'story-wrap', label: 'Story-Wrap (stories.app.goo.gl)' },
                  { value: 'search-app-wrap', label: 'Search-App-Wrap (search.app.goo.gl)' },
                  ...(enableMixedStoryMode && key === 'cta'
                    ? [{ value: MIXED_STORY_PROVIDER, label: 'Mixxed 37,5/62,5 → Story-Makros' }]
                    : []),
                  ...(enableQrCta && key === 'cta'
                    ? [{ value: 'hybrid-share-qr', label: 'Hybrid Share QR Code' }]
                    : []),
                  { value: 'aws+searchapp', label: 'AWS + Search.app' },
                  { value: 'share.google+searchapp', label: 'Share.Google + Search.app' }
                ]
              : [
                  { value: 'searchapp', label: 'Search.app' },
                  { value: 'searchapp-s3', label: 'Search.app‑S3' },
                  { value: 'share.google', label: 'Share.Google' },
                  { value: 'hybrid-share.google', label: 'Hybrid Share.Google' },
                  { value: 'share.google+searchapp', label: 'Share.Google + Search.app' }
                ]);
          const targetOptions = isAdmin
            ? [
                { value: 'slot', label: 'Slot' },
                { value: 'custom', label: 'Custom Targets' }
              ]
            : [
                { value: 'custom', label: 'Custom Targets' }
              ];
          return (
            <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <div className="font-medium text-gray-800 dark:text-gray-200">{label}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Provider
                  </label>
                  <select
                    value={provider}
                    onChange={(e) => {
                      const nextProvider = e.target.value;
                      setLinkConfig(prev => {
                        const updated = { ...prev[key], provider: nextProvider };
                        if (nextProvider === 'brandlogo' && !updated.logo_brand && availableBrands.length > 0) {
                          updated.logo_brand = availableBrands[0].slug;
                        }
                        return { ...prev, [key]: updated };
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {providerOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {provider !== 'brandlogo' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Target
                  </label>
                  <select
                    value={target}
                    onChange={(e) => {
                      const nextTarget = e.target.value;
                      setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], target: nextTarget }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {targetOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                )}
              </div>

              {provider === 'brandlogo' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Brand
                  </label>
                  <select
                    value={linkConfig[key]?.logo_brand || (availableBrands[0]?.slug || 'pobalogo')}
                    onChange={(e) => {
                      setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], logo_brand: e.target.value }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {availableBrands.length > 0 ? availableBrands.map(b => (
                      <option key={b.slug} value={b.slug}>{b.name} ({b.slug})</option>
                    )) : (
                      <>
                        <option value="pobalogo">Postbank</option>
                        <option value="sparklogo">Sparkasse</option>
                        <option value="vblogo">Volksbank</option>
                      </>
                    )}
                  </select>
                  <div className="flex items-center gap-2 mt-2">
                    <label className="block text-xs text-gray-500 dark:text-gray-400">Provider:</label>
                    <select
                      value={linkConfig[key]?.brand_provider || 'share.google'}
                      onChange={(e) => {
                        setLinkConfig(prev => ({
                          ...prev,
                          [key]: { ...prev[key], brand_provider: e.target.value }
                        }));
                      }}
                      className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="share.google">Share.Google</option>
                      <option value="search.app">Search.app</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Anzahl
                  </label>
                  <input
                    type="number"
                    value={linkCounts[key]}
                    onChange={(e) => updateLinkCount(key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="0"
                    max={Number.isFinite(maxForUser) ? maxForUser : undefined}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Preset
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {presetValues.map((value) => (
                      <button
                        key={`${key}_preset_${value}`}
                        type="button"
                        onClick={() => updateLinkCount(key, value)}
                        className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-gray-600"
                      >
                        {value === 0 ? 'None' : value}
                      </button>
                    ))}
                  </div>
                  {!isAdmin && Number.isFinite(maxForUser) && maxForUser !== Infinity && (
                    <div className="text-xs text-gray-500 mt-1">
                      Maximal {maxForUser} {(provider === 'aws' || provider === 'hybrid-aws-share.google') ? 'AWS' : 'Search.app‑S3'}‑Links pro Job.
                    </div>
                  )}
                </div>
              </div>

              {(provider === 'searchapp'
                || provider === 'searchapp-s3'
                || provider === 'aws+searchapp'
                || provider === 'hybrid-aws-share.google'
                || provider === MIXED_STORY_PROVIDER) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Link Style {(provider === 'aws+searchapp' || provider === 'hybrid-aws-share.google')
                      ? '(Search.app)'
                      : ''}
                  </label>
                  <select
                    value={current?.linkStyle || 'SHORT'}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], linkStyle: nextValue }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="SHORT">Short</option>
                    <option value="UNGUESSABLE">Unguessable</option>
                  </select>
                </div>
              )}

              {(provider === 'aws'
                || provider === 'searchapp-s3'
                || provider === 'aws+searchapp'
                || provider === 'hybrid-aws-share.google'
                || provider === MIXED_STORY_PROVIDER) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    AWS Regionen (Mixed, pro Segment rotierend) {provider === 'aws+searchapp' ? '(AWS)' : ''}
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], regions: [...availableDefaultAwsRegions] },
                      }))}
                      className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    >
                      Alle {availableDefaultAwsRegions.length}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], regions: [] },
                      }))}
                      className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    >
                      Keine
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                    {availableAwsRegionOptions.map((option) => {
                      const selected = Array.isArray(current?.regions)
                        ? current.regions.includes(option.value)
                        : availableDefaultAwsRegions.includes(option.value);
                      return (
                        <label key={`${key}_${option.value}`} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setLinkConfig(prev => {
                                const prevRegions = normalizeAwsRegions(
                                  prev[key]?.regions
                                );
                                const nextRegions = checked
                                  ? Array.from(new Set([...prevRegions, option.value]))
                                  : prevRegions.filter((value) => value !== option.value);
                                return {
                                  ...prev,
                                  [key]: { ...prev[key], regions: nextRegions },
                                };
                              });
                            }}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {normalizeAwsRegions(current?.regions).length} Region(en) aktiv — Segment N nutzt Region #{`(N mod ${normalizeAwsRegions(current?.regions).length})`}
                  </div>
                </div>
              )}

              {target === 'slot' && provider !== 'brandlogo' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {provider === MIXED_STORY_PROVIDER
                      ? 'AWS-Hybrid Slot'
                      : 'Slot auswählen'}
                  </label>
                  <select
                    value={current?.slotIndex || ''}
                    onChange={(e) => {
                      const nextIndex = e.target.value;
                      setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], slotIndex: nextIndex }
                      }));
                    }}
                    disabled={loadingSlots}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {loadingSlots ? (
                      <option>Lade Slots...</option>
                    ) : slots.length === 0 ? (
                      <option>Keine Slots verfügbar</option>
                    ) : (
                      <>
                        <option value="">-- Slot wählen --</option>
                        {slots.map((slot, idx) => (
                          <option key={idx} value={idx}>
                            {slot.type === 'landing' ? '🌐' : '🔀'} {slot.target_url || slot.name} ({slot.type === 'landing' ? `Landing · ${slot.total_domains || slot.domains?.length || 0} Domains` : `${slot.total_domains || slot.domains?.length || 0} Domains`})
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              )}

              {target === 'slot' && provider === MIXED_STORY_PROVIDER && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search-App-Wrap Slot
                  </label>
                  <select
                    value={current?.searchappWrapSlotIndex || ''}
                    onChange={(e) => {
                      const nextIndex = e.target.value;
                      setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], searchappWrapSlotIndex: nextIndex }
                      }));
                    }}
                    disabled={loadingSlots}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {loadingSlots ? (
                      <option>Lade Slots...</option>
                    ) : searchappSlots.length === 0 ? (
                      <option>Keine Search-App-Slots verfügbar</option>
                    ) : (
                      <>
                        <option value="">-- Search-App-Wrap Slot wählen --</option>
                        {searchappSlots.map((slot, idx) => (
                          <option key={idx} value={idx}>
                            {slot.type === 'landing' ? '🌐' : '🔀'} {slot.target_url || slot.name} ({slot.total_domains || slot.domains?.length || 0} Domains)
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  <div className="text-xs text-gray-500 mt-1">
                    37,5% AWS Hybrid · 62,5% Search-App-Wrap · kein Story-Wrap
                  </div>
                </div>
              )}

              {target === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ziel‑URLs (eine pro Zeile oder Komma)
                  </label>
                  <textarea
                    value={current?.customTargetsText || ''}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setLinkConfig(prev => ({
                        ...prev,
                        [key]: { ...prev[key], customTargetsText: nextValue }
                      }));
                    }}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="https://ziel-1.tld/offer&#10;https://ziel-2.tld/cta"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {customTargets.length} Ziel‑URL(s) erkannt
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* SMTP Email (optional) */}
      {usesSearchApp && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            SMTP Email (optional)
          </label>
          <input
            type="email"
            value={smtpEmail}
            onChange={(e) => setSmtpEmail(e.target.value)}
            placeholder="sender@domain.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      )}

      {/* Configure/Generate Button */}
      <button
        type="button"
        onClick={handleConfigureLinks}
        disabled={
          loadingSlots ||
          configured ||
          !isConfigReady()
        }
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
      >
        {loadingSlots ? (
          <>
            <RefreshCw className="w-5 h-5 animate-spin" />
            Generiere Links...
          </>
        ) : configured ? (
          <>
            <Check className="w-5 h-5" />
            Konfiguration gespeichert
          </>
        ) : jobId ? (
          <>
            <Link2 className="w-5 h-5" />
            Links Generieren
          </>
        ) : (
          <>
            <Link2 className="w-5 h-5" />
            Link-Konfiguration speichern
          </>
        )}
      </button>

      {/* Configuration Saved Summary */}
      {configured && !jobId && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                ✅ Link-Konfiguration gespeichert!
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-green-700 dark:text-green-300">Logo:</span>
                  <span className="ml-2 font-bold text-green-900 dark:text-green-100">
                    {linkCounts.logo}
                  </span>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300">CTA:</span>
                  <span className="ml-2 font-bold text-green-900 dark:text-green-100">
                    {linkCounts.cta}
                  </span>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300">Unsubscribe:</span>
                  <span className="ml-2 font-bold text-green-900 dark:text-green-100">
                    {linkCounts.unsubscribe}
                  </span>
                </div>
              </div>
              <p className="mt-2 text-xs text-green-700 dark:text-green-300">
                Die Links werden automatisch beim Job-Start generiert und per Round-Robin verteilt.
                <br />
                Verwende <code className="px-1 py-0.5 bg-green-100 dark:bg-green-800 rounded">{'{{AWSLOGO}}'}</code>, <code className="px-1 py-0.5 bg-green-100 dark:bg-green-800 rounded">{'{{AWSCTA}}'}</code>, <code className="px-1 py-0.5 bg-green-100 dark:bg-green-800 rounded">{'{{AWSUNSUBSCRIBE}}'}</code> in deiner Nachricht.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <p className="font-medium mb-1">Verwendung:</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>Links werden automatisch Round-Robin rotiert</li>
            <li>Jede E-Mail bekommt einen anderen Link aus dem Pool</li>
            <li>Macros: <code>{'{{AWSLOGO}}'}</code>, <code>{'{{AWSCTA}}'}</code>, <code>{'{{AWSUNSUBSCRIBE}}'}</code></li>
            <li>Oder: <code>{'{{SEARCHLOGO}}'}</code>, <code>{'{{SEARCHCTA}}'}</code></li>
            <li>Story/Search-App-Wrap: <code>{'{{STORYLOGO}}'}</code>, <code>{'{{STORYCTA}}'}</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminLinkPanel;
